import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import Button from "../Button";
import verify from "./Verify_Header.svg";
import refresh from "./refresh.svg";
import styles from "./VerifyEmailModal.module.css";
import emailSign from "../../assets/email.svg";
import warningIcon from "assets/warning_Icon_Red.png";
import { useTranslation } from "react-i18next";

export function VerifyEmailModal({ email, onClose, onVerify, onResendEmail }) {
  const [verificationCode, setVerificationCode] = useState(Array(6).fill(""));
  const [errorMessage, setErrorMessage] = useState("");
  const inputRefs = useRef(Array(6).fill(null));
  const {t} = useTranslation();

  //Handles the input change
  const handleInputChange = (index, value) => {
    const updatedCodes = [...verificationCode];
    updatedCodes[index] = value;

    setVerificationCode(updatedCodes);

    // Only move focus if the input is valid and not the last input
    if (value !== "" && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (
      event.key === "Backspace" &&
      index > 0 &&
      verificationCode[index] === ""
    ) {
      inputRefs.current[index - 1].focus();
    }
  };

  //Triggers the verify button
  const handleVerify = async (e) => {
    e.preventDefault();
    const code = verificationCode.join("");

    const isCodeValid = await onVerify(email, code, setErrorMessage);

    if (!isCodeValid) {
      setErrorMessage(t("Incorrect verification code"));
      return;
    }

    setErrorMessage("");
    onClose();
  };

  const handleRefreshClick = () => {
    onResendEmail();
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Button
          onClick={onClose}
          className={styles.close}
          buttonText={t("Back")}
        />

        <div className={styles.topContent}>
          <img src={verify} alt="Verify Icon" className={styles.icon} />
          <span className={styles.header}>{t("Let's Verify")}</span>
          <p className={styles.p1}>
            {t("Please take a moment to verify email")}
          </p>
          <p className={styles.p2}>
            {t("This helps us confirm your identity")}
          </p>
        </div>

        <div className={styles.cardContainer}>
          <div className={styles.emailSection}>
            <span className={`${styles.emailText}`}>
              <img src={emailSign} alt="At sign" className={styles.at} />{" "}
              {t("Email Address")}
            </span>
          </div>
          <div className={`${styles.border} ${styles.emailContent}`}></div>
          <div className={styles.cardContent}>
            {errorMessage ? (
              <div className={styles.errorMessageContainer}>
                <img
                  src={warningIcon}
                  alt="Warning Icon"
                  className={styles.errorIcon}
                />
                <span className={styles.errorMessage}>{errorMessage}</span>
              </div>
            ) : (
              <span className={styles.inputHeader}>{t("Enter your code")}</span>
            )}
            <form onSubmit={handleVerify}>
              <div className={styles.verificationCodeInput}>
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(input) => (inputRefs.current[index] = input)}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    required
                    className={styles.singleDigitInput}
                  />
                ))}
              </div>
            </form>
          </div>
          <div className={styles.border}>
            {t("We sent an email to")}{" "}
            <span className={styles.email}>{email}</span>
          </div>
          <div className={styles.resendText} onClick={onResendEmail}>
            <span>{t("Resend Email")}</span>
            <img
              src={refresh}
              alt="Refresh"
              onClick={handleRefreshClick}
              className={styles.resendIcon}
            />
          </div>
        </div>

        <Button buttonText={t("Verify")} onClick={handleVerify} />
      </div>
    </div>
  );
}

VerifyEmailModal.propTypes = {
  email: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onVerify: PropTypes.func.isRequired,
};

export default VerifyEmailModal;
